import React, { useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { createTemplateFolderApi } from '../useData';

interface CreateTemplateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  scope: 'PERSONAL' | 'COMPANY';
  parentId?: number | null;
  parentName?: string;
  onCreated: () => void | Promise<void>;
}

const CreateTemplateFolderModal: React.FC<CreateTemplateFolderModalProps> = ({
  isOpen,
  onClose,
  scope,
  parentId,
  parentName,
  onCreated,
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await createTemplateFolderApi({
        name: trimmed,
        scope,
        ...(parentId != null ? { parentId } : {}),
      });
      await onCreated();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>New {scope === 'COMPANY' ? 'company' : 'personal'} folder</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        {parentName && (
          <p className={cn('text-xs mb-3', colors.textMuted)}>
            Inside folder: <span className={cn('font-medium', colors.text)}>{parentName}</span>
          </p>
        )}
        <label className={cn('block text-sm font-medium mb-1.5', colors.text)} htmlFor="template-folder-name">
          Folder name
        </label>
        <input
          id="template-folder-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
          className={cn('w-full rounded-lg border px-3 py-2 text-sm', colors.border, colors.surface, colors.text)}
          placeholder="e.g. Sales outreach"
          autoFocus
        />
        {error && (
          <div className="mt-3 rounded-xl px-4 py-3 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="default" onClick={() => void handleSubmit()} disabled={saving || !name.trim()}>
          {saving ? 'Creating…' : 'Create folder'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateTemplateFolderModal;
